import React from "react";
import {
  StyleSheet,
  View,
  ImageSourcePropType,
  TouchableOpacity,
  Text,
} from "react-native";
import { Typography, Badge } from "src/components";
import { colors } from "src/assets";
import { User, Price, OfferAvatar, Rating, Likes, PV } from "./components";
import { UserStatus } from "src/domains";
import { trim } from "src/utils/string";
import { stringDateFullDynamicFormat } from "src/utils/date";
import LocalText from "src/local";

export type OfferListItemLargeProps = {
  userName?: string;
  userImageSource?: ImageSourcePropType | undefined;
  offerName: string;
  offerImageSource?: ImageSourcePropType | undefined;
  categoryName?: string | undefined;
  price: number;
  rate?: number;
  rateCount?: number;
  likes?: number;
  status?: UserStatus;
  pv?: number;
  isPublic?: number;
  createdAt?: string;
  isVerified?: boolean;
  onPress?: () => void;
};

export const OfferListItemLarge: React.FC<OfferListItemLargeProps> = ({
  offerName,
  offerImageSource,
  userName,
  userImageSource,
  categoryName,
  rate,
  rateCount,
  likes,
  price,
  status,
  pv,
  onPress,
  isPublic,
  isVerified,
  createdAt,
}) => {
  return (
    <TouchableOpacity
      style={styles.root}
      onPress={() => (onPress ? onPress() : {})}
    >
      <View style={[styles.row, styles.flex]}>
        <View
          style={[
            styles.col,
            styles.justifyContentSpaceBetween,
            styles.flex,
            { marginRight: 16 },
          ]}
        >
          <View style={[styles.col]}>
            {isPublic !== undefined && (
              <View style={[styles.col, styles.mb1]}>
                {isPublic === 1 && (
                  <Badge
                    text={LocalText.components.offerListItem.public}
                    color={colors.chokuGreen}
                  />
                )}
                {isPublic === 0 && (
                  <Badge
                    text={LocalText.components.offerListItem.unpublic}
                    color={colors.chokuGray6}
                  />
                )}
              </View>
            )}
            {userName ? (
              <User
                source={userImageSource}
                status={status}
                name={userName}
                isVerified={isVerified}
              />
            ) : null}
            <Typography variant={"h1"} text={trim(offerName, 32, "...")} />
          </View>
          <View style={styles.col}>
            {createdAt && (
              <View style={[styles.createdAtContainer, styles.mt1]}>
                <Text style={styles.createdAtLabel}>
                  {LocalText.components.offerListItem.createdAt}
                </Text>
                <Text style={styles.createdAtValue}>
                  {stringDateFullDynamicFormat(createdAt)}
                </Text>
              </View>
            )}
            {categoryName ? (
              <Badge
                containerStyle={styles.badge}
                text={categoryName}
                color={colors.chokuGray5}
              />
            ) : null}
            <Rating
              style={styles.rating}
              value={rate || 0}
              count={rateCount || 0}
            />
          </View>
        </View>
        <OfferAvatar source={offerImageSource} status={status} />
      </View>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <View>
          <Likes value={likes || 0} />
        </View>
        <View>
          <Price value={price} />
        </View>
      </View>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <View>{pv !== undefined ? <PV value={pv} /> : null}</View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  root: {
    flexDirection: "column",
    justifyContent: "flex-start",
    backgroundColor: colors.white,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomColor: colors.gray1,
    borderBottomWidth: 1,
  },
  flex: {
    flex: 1,
  },
  row: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  col: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  justifyContentSpaceBetween: {
    justifyContent: "space-between",
  },
  badge: {
    marginTop: 16,
  },
  rating: {
    marginTop: 14,
  },
  mt1: {
    marginTop: 8,
  },
  mb1: {
    marginBottom: 8,
  },
  publicStatus: {
    borderRadius: 12,
    borderWidth: 2,
    padding: 4,
  },
  publicStatusPublished: {
    borderColor: colors.chokuGreen,
  },
  publicStatusUnpublished: {
    borderColor: colors.chokuGray6,
  },
  createdAtContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  createdAtLabel: {
    fontSize: 14,
    color: colors.chokyGray10,
  },
  createdAtValue: {
    fontSize: 14,
    color: colors.chokuBlack,
  },
});
